<script lang="ts">
  import { navigate } from '@shared/router/navigation';
  import type { Problem, ProblemListQuery } from '@shared/types/problem';

  type SortField = NonNullable<ProblemListQuery['sort_by']>;
  type SortDir = NonNullable<ProblemListQuery['sort_dir']>;

  interface Props {
    problems: Problem[];
    sortBy: SortField;
    sortDir: SortDir;
    onSort: (field: SortField) => void;
  }

  let { problems, sortBy, sortDir, onSort }: Props = $props();

  interface Column {
    key: SortField | null;
    label: string;
  }

  const columns: Column[] = [
    { key: null, label: '大カテゴリ' },
    { key: null, label: '中テーマ' },
    { key: 'name', label: '小課題名' },
    { key: null, label: '推認属性' },
    { key: null, label: '推認キーワード' },
  ];

  function indicator(field: SortField | null): string {
    if (field === null || field !== sortBy) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  function gotoDetail(ev: MouseEvent, id: string) {
    ev.preventDefault();
    navigate(`/master/problems/${id}`);
  }

  function isMissingValue(value: string | null): boolean {
    return value === null || value.trim().length === 0;
  }

  // src 側 problems.astro と同じロジック: 文字列配列・構造化オブジェクトのどちらでも落ちないようにする。
  function formatDetectionSignals(signals: unknown): string[] {
    if (signals === null || signals === undefined) return [];
    if (Array.isArray(signals)) {
      return signals
        .map((s): string => {
          if (typeof s === 'string') return s;
          if (s && typeof s === 'object') {
            const rec = s as Record<string, unknown>;
            const field = typeof rec.field === 'string' ? rec.field : '';
            const op = typeof rec.operator === 'string' ? rec.operator : '=';
            const value = rec.value;
            return `${field} ${op} ${String(value ?? '')}`.trim();
          }
          return String(s);
        })
        .filter((s) => s.length > 0);
    }
    if (typeof signals === 'string') {
      return signals.split(/\r?\n/).filter((s) => s.length > 0);
    }
    return [];
  }

  function formatKeywordPreview(kw: string | null): string {
    if (!kw) return '';
    const items = kw
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (items.length === 0) return '';
    if (items.length <= 3) return items.join(' / ');
    return `${items.slice(0, 3).join(' / ')} 他 ${items.length - 3} 件`;
  }
</script>

<table>
  <colgroup>
    <col class="category-column" />
    <col class="theme-column" />
    <col class="name-column" />
    <col class="signal-column" />
    <col class="keyword-column" />
  </colgroup>
  <thead>
    <tr>
      {#each columns as col (col.label)}
        <th>
          {#if col.key}
            <button
              type="button"
              class="sort"
              onclick={() => onSort(col.key as SortField)}
            >
              {col.label}{indicator(col.key)}
            </button>
          {:else}
            {col.label}
          {/if}
        </th>
      {/each}
    </tr>
  </thead>
  <tbody>
    {#each problems as p (p.id)}
      {@const signals = formatDetectionSignals(p.detection_signals)}
      {@const keywordPreview = formatKeywordPreview(p.detection_keywords)}
      <tr>
        <td>
          <div class="category-cell">
            <span>{p.category}</span>
            {#if isMissingValue(p.category)}
              <span class="warning-badge">大カテゴリ未設定</span>
            {/if}
          </div>
        </td>
        <td>
          <div class="category-cell">
            <span>{p.theme}</span>
            {#if isMissingValue(p.theme)}
              <span class="warning-badge">中テーマ未設定</span>
            {/if}
          </div>
        </td>
        <td>
          <strong>
            <a
              href={`/master/problems/${p.id}`}
              onclick={(ev) => gotoDetail(ev, p.id)}
            >
              {p.name}
            </a>
          </strong>
        </td>
        <td>
          {#if signals.length === 0}
            <span class="muted small">-</span>
          {:else}
            <div class="signal-preview">
              {#each signals.slice(0, 2) as s (s)}
                <span class="signal-chip">{s}</span>
              {/each}
              {#if signals.length > 2}
                <span class="muted small">+{signals.length - 2}</span>
              {/if}
            </div>
          {/if}
        </td>
        <td>
          {#if keywordPreview}
            <span class="keyword-preview" title={p.detection_keywords ?? ''}>
              {keywordPreview}
            </span>
          {:else}
            <span class="muted small">-</span>
          {/if}
        </td>
      </tr>
    {/each}
  </tbody>
</table>

<style>
  table {
    width: 100%;
    min-width: 980px;
    table-layout: fixed;
  }
  .category-column {
    width: 11%;
  }
  .theme-column {
    width: 12%;
  }
  .name-column {
    width: 44%;
  }
  .signal-column {
    width: 17%;
  }
  .keyword-column {
    width: 16%;
  }
  th {
    text-align: center;
  }
  td {
    vertical-align: middle;
  }
  td:first-child,
  td:nth-child(2),
  td:nth-child(4) {
    text-align: center;
  }
  td:nth-child(3),
  td:nth-child(5) {
    text-align: left;
  }
  button.sort {
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    padding: 0;
    cursor: pointer;
  }
  .category-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  td:first-child .category-cell,
  td:nth-child(2) .category-cell {
    flex-wrap: nowrap;
    justify-content: center;
  }
  td:first-child .category-cell > span:first-child,
  td:nth-child(2) .category-cell > span:first-child {
    white-space: nowrap;
  }
  .warning-badge {
    display: inline-flex;
    align-items: center;
    min-height: 22px;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid #fcd34d;
    background: #fffbeb;
    color: #92400e;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
  }
  .signal-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    align-items: center;
    justify-content: center;
  }
  .signal-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 6px;
    background: #f1f5f9;
    color: #334155;
    font-size: 11px;
    white-space: nowrap;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .keyword-preview {
    display: inline-block;
    color: #475569;
    font-size: 12px;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .small {
    font-size: 11px;
  }
</style>
